import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { apiService, CompanyResource, TenantDatabaseRequest } from '../services/api';
import toast from 'react-hot-toast';
import { CloudIcon } from '@heroicons/react/24/outline';
import Modal from '../components/Modal';

const Infrastructure: React.FC = () => {
  usePageTitle('Resources');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<CompanyResource | null>(null);
  const companyId = user?.company_id || '';

  // Database BYO state
  const [dbUseConnectionString, setDbUseConnectionString] = useState(true);
  const [dbConnectionString, setDbConnectionString] = useState('');
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('5432');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbDatabase, setDbDatabase] = useState('');
  const [dbSslMode, setDbSslMode] = useState('prefer');
  const [dbTestLoading, setDbTestLoading] = useState(false);
  const [dbSaveLoading, setDbSaveLoading] = useState(false);
  const [dbTestError, setDbTestError] = useState<string | null>(null);
  const [dbTestSuccess, setDbTestSuccess] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [backupBeforeReset, setBackupBeforeReset] = useState(true);
  const [dbBackupLoading, setDbBackupLoading] = useState(false);
  const [dbResetLoading, setDbResetLoading] = useState(false);

  const loadResources = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getCompanyResources(companyId);
      setResources(data);
    } catch (error: any) {
      console.error('Failed to load company resources:', error);
      toast.error('Failed to load resources configuration');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) loadResources();
  }, [companyId, loadResources]);

  const buildTenantDbRequest = (): TenantDatabaseRequest => {
    if (dbUseConnectionString) return { connection_string: dbConnectionString.trim() };
    return { host: dbHost.trim(), port: dbPort.trim() || '5432', user: dbUser.trim(), password: dbPassword, database: dbDatabase.trim(), sslmode: dbSslMode || 'prefer' };
  };

  const handleTestTenantDb = async () => {
    if (!companyId) return;
    const req = buildTenantDbRequest();
    if (dbUseConnectionString ? !req.connection_string : !req.host || !req.user || !req.database) {
      toast.error('Please fill in connection string or host, user, and database');
      return;
    }
    setDbTestError(null);
    setDbTestSuccess(false);
    setDbTestLoading(true);
    try {
      await apiService.testTenantDatabase(companyId, req);
      setDbTestSuccess(true);
      toast.success('Connection successful');
    } catch (e: any) {
      setDbTestError(e.response?.data?.error || e.message || 'Connection failed');
      toast.error(e.response?.data?.error || 'Connection failed');
    } finally {
      setDbTestLoading(false);
    }
  };

  const handleSetTenantDb = async () => {
    if (!companyId) return;
    const req = buildTenantDbRequest();
    if (dbUseConnectionString ? !req.connection_string : !req.host || !req.user || !req.database) {
      toast.error('Please fill in connection string or host, user, and database');
      return;
    }
    setDbSaveLoading(true);
    setDbTestError(null);
    try {
      await apiService.setTenantDatabase(companyId, req);
      toast.success('Database configuration saved');
      loadResources();
    } catch (e: any) {
      setDbTestError(e.response?.data?.error || e.message || 'Save failed');
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setDbSaveLoading(false);
    }
  };

  const handleBackupTenantDb = async () => {
    if (!companyId) return;
    setDbBackupLoading(true);
    try {
      const { blob, filename } = await apiService.getTenantDatabaseBackup(companyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Backup failed');
    } finally {
      setDbBackupLoading(false);
    }
  };

  const handleResetTenantDb = async () => {
    if (!companyId) return;
    if (backupBeforeReset) {
      setDbResetLoading(true);
      try {
        const { blob, filename } = await apiService.getTenantDatabaseBackup(companyId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Backup downloaded');
      } catch (e: any) {
        toast.error(e.response?.data?.error || e.message || 'Backup failed');
        setDbResetLoading(false);
        return;
      }
      setDbResetLoading(false);
    }
    setResetModalOpen(false);
    setDbResetLoading(true);
    try {
      await apiService.resetTenantDatabase(companyId);
      toast.success('Database reset and re-initialized');
      loadResources();
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Reset failed');
    } finally {
      setDbResetLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  const canSpinUp = resources?.allow_company_admins_spin_up_db === true;
  const canBringOwn = resources?.allow_company_admins_bring_own_db !== false; // default true for backward compat when flag missing
  const canBackupDb = user?.role === 'sysadmin' || (resources?.allow_company_admins_backup_db === true);
  const noOptions = !canSpinUp && !canBringOwn;

  return (
    <div className="space-y-6">
      {noOptions && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            No database options are enabled for your organisation. Contact your administrator.
          </p>
        </div>
      )}

      {canSpinUp && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6">
          <div className="flex items-center gap-3">
            <CloudIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Spin up new database</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Governate Cloud SQL. Coming soon.</p>
            </div>
          </div>
        </div>
      )}

      {canBringOwn && (resources?.database_mode === 'self' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-5">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            Your organisation is configured to use its own database (connection details stored encrypted).
          </p>
          {canBackupDb && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleBackupTenantDb} disabled={dbBackupLoading} className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors">
                {dbBackupLoading ? 'Preparing...' : 'Backup database'}
              </button>
              <button type="button" onClick={() => setResetModalOpen(true)} disabled={dbResetLoading} className="inline-flex items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-3 py-2 text-sm font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors">
                {dbResetLoading ? 'Resetting...' : 'Reset database'}
              </button>
            </div>
          )}
          <Modal isOpen={resetModalOpen} onClose={() => setResetModalOpen(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Reset database</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                This will run reset.sql then init.sql on your database. All tables and data will be dropped and the schema recreated. This cannot be undone.
              </p>
              <label className="flex items-center gap-2 mb-4 cursor-pointer">
                <input type="checkbox" checked={backupBeforeReset} onChange={(e) => setBackupBeforeReset(e.target.checked)} className="rounded text-governate-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Download backup before reset</span>
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setResetModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="button" onClick={handleResetTenantDb} disabled={dbResetLoading} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">OK, reset</button>
              </div>
            </div>
          </Modal>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Connect your own PostgreSQL. Test then save — we run schema init on save.</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">Host must be reachable from the server (e.g. <code className="font-mono">postgres</code> in Docker, not <code className="font-mono">localhost</code>).</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={dbUseConnectionString} onChange={() => { setDbUseConnectionString(true); setDbTestError(null); }} className="text-governate-600" />
              <span className="text-sm font-medium">Connection string</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!dbUseConnectionString} onChange={() => { setDbUseConnectionString(false); setDbTestError(null); }} className="text-governate-600" />
              <span className="text-sm font-medium">Individual fields</span>
            </label>
          </div>
          {dbUseConnectionString ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PostgreSQL connection string</label>
              <textarea value={dbConnectionString} onChange={(e) => { setDbConnectionString(e.target.value); setDbTestError(null); }} placeholder="postgres://user:password@host:5432/database?sslmode=require" rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 font-mono text-sm bg-white dark:bg-gray-800" />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Host</label><input type="text" value={dbHost} onChange={(e) => setDbHost(e.target.value)} placeholder="postgres" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port</label><input type="text" value={dbPort} onChange={(e) => setDbPort(e.target.value)} placeholder="5432" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">User</label><input type="text" value={dbUser} onChange={(e) => setDbUser(e.target.value)} placeholder="postgres" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label><input type="password" value={dbPassword} onChange={(e) => setDbPassword(e.target.value)} placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Database</label><input type="text" value={dbDatabase} onChange={(e) => setDbDatabase(e.target.value)} placeholder="mydb" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800" /></div>
              <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SSL mode</label><select value={dbSslMode} onChange={(e) => setDbSslMode(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 bg-white dark:bg-gray-800"><option value="disable">disable</option><option value="prefer">prefer</option><option value="require">require</option><option value="verify-full">verify-full</option></select></div>
            </div>
          )}
          {dbTestError && <p className="text-sm text-red-600 dark:text-red-400">{dbTestError}</p>}
          {dbTestSuccess && <p className="text-sm text-green-600 dark:text-green-400">Connection successful. You can save below.</p>}
          <div className="flex gap-3">
            <button type="button" onClick={handleTestTenantDb} disabled={dbTestLoading} className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">{dbTestLoading ? 'Testing...' : 'Test connection'}</button>
            <button type="button" onClick={handleSetTenantDb} disabled={dbSaveLoading} className="px-3 py-2 text-sm font-medium rounded-lg bg-governate-600 text-white hover:bg-governate-700 disabled:opacity-50">{dbSaveLoading ? 'Saving...' : 'Save & use this database'}</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Infrastructure;

